/* eslint-disable */
import React, { useState, useEffect } from "react";
import { useSearchResults, updateSearchResults } from "../common/redux/search";
import {
  useSearchContent,
  updateSearchContent,
} from "../common/redux/searchContent";
import ResultCount from "./components/result-count";
import Facets from "./components/facets";
import { postSearchResultClick } from "../common/api/searchApi";
import {
  IFacet,
  IFilterRequest,
  ISearchRequest,
  ITerm,
} from "../common/api/searchApi.types";
import { Query } from "../common/utils/environment/query";
import SearchBar from "../common/components/Search/searchBar";
import Pagination from "../common/components/Pagination/pagination";
import SearchResults from "./components/search-results";
import { getLanguageCookie } from "../registration/utils/helpers";
import LoadingSpinner from "../common/components/LoadingSpinner/LoadingSpinner";

const Search: React.FC<{}> = () => {
  const DefaultSearchTerm = "";
  const DefaultPageNumber = 1;
  const DefaultSelectedTerms: ITerm[] = [];

  const [searchTerm, setSearchTerm] = useState<string>(DefaultSearchTerm);
  const [pageSize, setPageSize] = useState<number>(10);
  const [pageNumber, setPageNumber] = useState<number>(DefaultPageNumber);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [selectedTerms, setSelectedTerms] =
    useState<ITerm[]>(DefaultSelectedTerms);
  const [fetchError, setFetchError] = useState<boolean>(false);
  const searchResults = useSearchResults();
  const searchContent = useSearchContent();

  // Read and initialize from query string state on load
  useEffect(() => {
    const initialQueryParams = Query.getParams();

    const querySearchTerm =
      (initialQueryParams.q &&
        decodeURIComponent(initialQueryParams.q[0].replace("+", " "))) ||
      "";
    setSearchTerm(querySearchTerm);
    delete initialQueryParams.q;

    const queryPageNumber =
      (initialQueryParams.pageNumber && +initialQueryParams.pageNumber[0]) || 1;
    setPageNumber(queryPageNumber);
    delete initialQueryParams.pageNumber;

    const querySelectedTerms = initialQueryParams.selectedTerms
      ? initialQueryParams.selectedTerms.map((t) =>
          JSON.parse(decodeURIComponent(t))
        )
      : [];

    setSelectedTerms(querySelectedTerms);
    delete initialQueryParams.selectedTerms;

    updateSearchContent(getLanguageCookie());

    if (
      querySearchTerm !== DefaultSearchTerm ||
      queryPageNumber !== DefaultPageNumber ||
      querySelectedTerms !== DefaultSelectedTerms
    ) {
      getSearchResults(
        querySearchTerm,
        queryPageNumber,
        toFilterRequest(querySelectedTerms)
      );
    }
  }, []);

  // Update query string params
  useEffect(() => {
    updateQueryParams();
  }, [searchTerm, pageNumber, selectedTerms]);

  const onResultClick = (hitId: string) => {
    postSearchResultClick({
      hitId,
      searchTerm,
      trackingId: searchResults.trackingId,
    });
  };

  const getSearchResults = (
    currentSearchTerm: string,
    currentPageNumber: number,
    currentFacets: IFilterRequest[]
  ) => {
    setIsLoading(true);
    setHasSearched(true);
    updateSearchResults({
      SearchTerm: currentSearchTerm,
      PageSize: pageSize,
      PageNumber: currentPageNumber,
      Filters: currentFacets,
      Language: getLanguageCookie(),
    } as ISearchRequest)
      .then(() => {
        setIsLoading(false);
        setFetchError(false);
      })
      .catch(() => {
        setIsLoading(false);
        setFetchError(true);
      });
  };

  const toFilterRequest = (terms: ITerm[]): IFilterRequest[] => {
    const facetMap = new Map<string, string[]>();
    [].forEach.call(terms, (term: ITerm) => {
      if (facetMap.has(term.fieldName)) {
        facetMap.get(term.fieldName)!.push(term.term);
      } else {
        facetMap.set(term.fieldName, [term.term]);
      }
    });

    return Array.from(facetMap.keys()).map((key) => ({
      fieldName: key,
      terms: facetMap.get(key),
    }));
  };

  const onSearchClick = (value: string) => {
    setPageNumber(1);
    setSelectedTerms([]);
    setSearchTerm(value);
    getSearchResults(value, 1, []);
  };

  const onPageSelect = (value: number) => {
    setPageNumber(value);
    getSearchResults(searchTerm, value, toFilterRequest(selectedTerms));
  };

  const setFacet = (id: string) => {
    let currentSelectedTerms: ITerm[] = [];
    const selectedTermIds = selectedTerms.map((term) => term.id);
    currentSelectedTerms = [...selectedTerms];
    const index = selectedTermIds.indexOf(id);
    if (index >= 0) {
      currentSelectedTerms.splice(index, 1);
    } else {
      let allTerms: ITerm[] = [];
      // .reduce((terms: ITerm[], facet: IFacet) => [...terms, ...facet.terms], [])
      [].forEach.call(searchResults.facets.facets, (facet: IFacet) => {
        allTerms = allTerms.concat(facet.terms);
      });
      currentSelectedTerms.push(allTerms.filter((t) => t.id === id)[0]);
    }

    setSelectedTerms(currentSelectedTerms);

    setPageNumber(1);
    getSearchResults(searchTerm, 1, toFilterRequest(currentSelectedTerms));
  };

  const resetFacets = () => {
    setSelectedTerms([]);
    setPageNumber(1);
    getSearchResults(searchTerm, 1, []);
  };

  const updateQueryParams = () => {
    const query: { [key: string]: string[] } = {};

    if (searchTerm.length > 0) {
      query.q = [searchTerm];
    }

    if (pageNumber) {
      query.pageNumber = [pageNumber.toString()];
    }

    if (selectedTerms) {
      query.selectedTerms = selectedTerms.map((term) =>
        encodeURIComponent(
          JSON.stringify(
            Object.freeze({
              id: term.id,
              fieldName: term.fieldName,
              term: term.term,
            })
          )
        )
      );
    }

    // Update the url
    Query.setParams(query);
  };

  const showRestrictedContent = () => {
    return searchContent.restrictedContentText !== null &&
    searchContent.restrictedContentText.length > 0 &&
    !isLoading &&
    hasSearched &&
    searchResults.hasRestrictedResults &&
    !fetchError;
  }

  return (
    <>
      <div className="wrapper-1440">
        <SearchBar
          defaultSearchTerm={searchTerm}
          onSubmit={onSearchClick}
          clearText={searchContent.clearButtonLabel}
          submitText={searchContent.searchButtonLabel}
          searchPlaceholder={searchContent.searchBarPlaceholder}
          showCloseButton={false}
          hideSearchBarContainer
        />
      </div>
      <div className="wrapper-1180 wrapper-mb-90 wrapper-top--search-open">
        <div className="container-fluid search-results-container">
          <div className="m-0 row">
            {isLoading && (
              <div className="col-12 p-0">
                <LoadingSpinner />
              </div>
            )}
            {!isLoading &&
              hasSearched &&
              searchResults.totalResults === 0 &&
              !fetchError && (
                <div
                  className="col-12 pt-5"
                  dangerouslySetInnerHTML={{
                    __html: searchContent.noResultsText,
                  }}
                />
              )}
            {fetchError && (
              <div
                className="col-12 pt-5"
                dangerouslySetInnerHTML={{
                  __html: searchContent.errorResultsText,
                }}
              />
            )}
            {!isLoading && searchResults.totalResults > 0 && !fetchError && (
              <>
                <div className="col-12 p-0">
                  <h1 className="heading--1 search-results-heading">
                    {searchContent.heading}
                  </h1>
                  <ResultCount
                    currentPage={pageNumber}
                    isLoading={isLoading}
                    pageSize={pageSize}
                    searchTerm={searchTerm}
                    totalResults={searchResults.totalResults}
                    currentPageResults={
                      searchResults.results ? searchResults.results.length : 0
                    }
                    numberOfResultsText={searchContent.pageNumbersText}
                  />
                </div>
                <div className="col-12 col-md-3 p-0">
                  <Facets
                    facets={searchResults.facets}
                    onResetClick={resetFacets}
                    onTermClick={setFacet}
                    selectedTerms={selectedTerms}
                  />
                </div>
                <div className="col-12 col-md-8 offset-0 offset-md-1 p-0">
                  {/* Restricted content banner */}
                  {showRestrictedContent() && (
                    <div className="wrapper-1180 mb-5">
                      <div className="container-fluid description-theme-light-blue p-0">
                        <div className="description-bg description-padding row m-0">
                          <div
                            className="pl-5 pr-5 col-12 p__body"
                            dangerouslySetInnerHTML={{
                              __html: searchContent.restrictedContentText,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <SearchResults
                    onResultClick={onResultClick}
                    results={searchResults.results}
                  />
                  <div className="container-fluid p-0">
                    <div className="m-0 row">
                      <div className="col-12 p-0">
                        <Pagination
                          onPageSelect={onPageSelect}
                          totalItems={searchResults.totalResults}
                          pageNumber={pageNumber}
                          pageSize={pageSize}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Search;
